import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HOME, PRIVATE } from '@core/constants/RoutingsPath';
import { UserRole } from '@core/models';
import { languageChanged } from '@core/modules/language/features/language.slice';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  completeUserProfile,
  isNewClient,
  loadUser,
  loadUserFulfilled,
  loadUserInterests,
  loadUserRejected,
  loggedIn,
  logout,
  refreshUser,
  selectUser,
  updateSubscription,
  updateUser,
  updateUserFulfilled,
  updateUserRejected,
  userChange
} from '../features/user.slice';
import { BasicInformation, Step } from '../models';
import { RootState } from 'app/app.module';
import { UserService } from '@core/modules/user/services/user.service';
import { NotifierService } from '@core/services/notifier.service';
import { PushService } from '@core/services/push/push.service';
import { RedirectAfterLoginService } from '@core/services/redirect-after-login.service';
import { DialogService } from '@shared/dialog/services/dialog.service';

@Injectable()
export class UserEffects {
  loggedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedIn),
      map((action) => loadUser(action.payload))
    )
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUser),
      concatLatestFrom(() => this.store.select(selectUser)),
      switchMap(([action, user]) =>
        (!user ? this.userService.getUser(action.payload) : of(user)).pipe(
          map((user) => loadUserFulfilled(user)),
          catchError((err) => {
            return of(loadUserRejected());
          })
        )
      )
    )
  );

  refreshUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshUser),
      switchMap((action) =>
        this.userService.getUser(action.payload).pipe(
          map((user) => loadUserFulfilled(user)),
          catchError(() => of(loadUserRejected()))
        )
      )
    )
  );

  loadUserFulfilled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserFulfilled),
      map((action) => action.payload),
      filter((user) => user.roleId === UserRole.CLIENT),
      map(() => loadUserInterests())
    )
  );

  // Push permission is requested AFTER login (never at app boot without a
  // user): loadUserFulfilled fires both on fresh login and on app start with a
  // stored token, which also refreshes the device token in the backend.
  registerPushNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserFulfilled),
        tap(() => this.pushService.requestPermissionAndRegister())
      ),
    { dispatch: false }
  );

  unregisterPushNotifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => this.pushService.unregister())
      ),
    { dispatch: false }
  );

  userChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userChange),
      concatLatestFrom(() => this.store.select((state) => state as RootState)),
      map(([action, rootState]) => ({
        action,
        currentStep: rootState.user.isNewClient
          ? rootState.captivePortal.currentStep
          : rootState.profile.currentStep
      })),
      filter(({ currentStep }) => currentStep === Step.BASIC_INFORMATION),
      map(({ action }) =>
        languageChanged({
          langId: (action.payload as BasicInformation).langId!,
          countryId: (action.payload as BasicInformation).countryId!
        })
      )
    )
  );

  completeUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(completeUserProfile),
      concatLatestFrom(() => [this.store.select(selectUser)]),
      switchMap(([action, user]) =>
        this.userService
          .completeUserProfile(
            !!action.payload.userData ? { ...user!, ...action.payload.userData } : user!
          )
          .pipe(
            tap((user) => {
              this.notifierService.openSuccessToast('profile.update.success');

              // Redirect to url which was set on register page
              if (user.redirectUrl) {
                window.location.replace(user.redirectUrl);
                return;
              }

              // The onboarding swallowed the deep link the user came in with.
              this.router.navigateByUrl(
                this.redirectAfterLoginService.consume() ?? `/${PRIVATE}/${HOME}`
              );
            }),
            map((user) => updateUserFulfilled(user)),
            catchError(() => of(updateUserRejected()))
          )
      )
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUser),
      concatLatestFrom(() => [this.store.select(selectUser), this.store.select(isNewClient)]),
      switchMap(([action, user, isNewClient]) =>
        this.userService
          .updateUser(!!action.payload.userData ? { ...user!, ...action.payload.userData } : user!)
          .pipe(
            tap((user) => {
              this.notifierService.openSuccessToast(
                action.payload.message ?? 'profile.update.success'
              );

              if (action.payload.informer) {
                this.dialogService.openConfirm(action.payload.informer);
              }

              // Redirect to url which was set on register page
              if (user.redirectUrl) {
                window.location.replace(user.redirectUrl);
                return;
              }

              isNewClient && this.router.navigate([`/${PRIVATE}/${HOME}`]);
            }),
            map((user) => updateUserFulfilled(user)),
            catchError(() => of(updateUserRejected()))
          )
      )
    )
  );

  addSubscription = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateSubscription),
        concatLatestFrom(() => this.store.select(selectUser)),
        // map(([action, user]) => [action.payload, user]),
        switchMap(([action, user]) => {
          if (action.payload.isActive) {
            return this.userService.removeSubscription(user!.id, action.payload.type_id).pipe();
          }
          return this.userService.addSubscription(user!.id, action.payload.type_id).pipe();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly userService: UserService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly notifierService: NotifierService,
    private readonly dialogService: DialogService,
    private readonly pushService: PushService,
    private readonly redirectAfterLoginService: RedirectAfterLoginService
  ) {}
}
